import { HDate, Location, Sedra } from "@hebcal/core";

// Jerusalem coordinates for calculations
const JERUSALEM_LOCATION = new Location(31.7683, 35.2137, false, 'Asia/Jerusalem');

// Hebrew parsha names mapping
const HEBREW_PARSHA_NAMES: Record<string, string> = {
  'Bereshit': 'בראשית',
  'Noach': 'נח',
  'Lech-Lecha': 'לך לך',
  'Vayera': 'וירא',
  'Chayei Sara': 'חיי שרה',
  'Toldot': 'תולדות',
  'Vayetzei': 'ויצא',
  'Vayishlach': 'וישלח',
  'Vayeshev': 'וישב',
  'Miketz': 'מקץ',
  'Vayigash': 'ויגש',
  'Vayechi': 'ויחי',
  'Shemot': 'שמות',
  'Vaera': 'וארא',
  'Bo': 'בא',
  'Beshalach': 'בשלח',
  'Yitro': 'יתרו',
  'Mishpatim': 'משפטים',
  'Terumah': 'תרומה',
  'Tetzaveh': 'תצוה',
  'Ki Tisa': 'כי תשא',
  'Vayakhel': 'ויקהל',
  'Pekudei': 'פקודי',
  'Vayikra': 'ויקרא',
  'Tzav': 'צו',
  'Shmini': 'שמיני',
  'Tazria': 'תזריע',
  'Metzora': 'מצורע',
  'Achrei Mot': 'אחרי מות',
  'Kedoshim': 'קדושים',
  'Emor': 'אמור',
  'Behar': 'בהר',
  'Bechukotai': 'בחקתי',
  'Bamidbar': 'במדבר',
  'Nasso': 'נשא',
  'Beha\'alotcha': 'בהעלותך',
  'Sh\'lach': 'שלח לך',
  'Korach': 'קרח',
  'Chukat': 'חקת',
  'Balak': 'בלק',
  'Pinchas': 'פינחס',
  'Matot': 'מטות',
  'Masei': 'מסעי',
  'Devarim': 'דברים',
  'Vaetchanan': 'ואתחנן',
  'Eikev': 'עקב',
  'Re\'eh': 'ראה',
  'Shoftim': 'שופטים',
  'Ki Teitzei': 'כי תצא',
  'Ki Tavo': 'כי תבא',
  'Nitzavim': 'נצבים',
  'Vayeilech': 'וילך',
  'Ha\'Azinu': 'האזינו',
  'V\'Zot HaBerachah': 'וזאת הברכה'
};

// Hebrew month names mapping
const HEBREW_MONTH_NAMES: Record<string, string> = {
  'Tishrei': 'תשרי',
  'Cheshvan': 'חשון',
  'Kislev': 'כסלו',
  'Tevet': 'טבת',
  'Shevat': 'שבט',
  'Adar': 'אדר',
  'Adar I': 'אדר א׳',
  'Adar II': 'אדר ב׳',
  'Nisan': 'ניסן',
  'Iyyar': 'אייר',
  'Sivan': 'סיוון',
  'Tammuz': 'תמוז',
  'Av': 'אב',
  'Elul': 'אלול'
};

function calculateSunset(date: Date): Date {
  try {
    // Simple sunset approximation for Jerusalem
    const sunset = new Date(date);
    
    // Adjust for season - more accurate approximation
    const month = date.getMonth() + 1;
    if (month >= 4 && month <= 9) {
      // Summer months - later sunset (7:45 PM)
      sunset.setHours(19, 45, 0, 0);
    } else {
      // Winter months - earlier sunset (5:15 PM)  
      sunset.setHours(17, 15, 0, 0);
    }
    
    return sunset;
  } catch (error) {
    console.error('Error in sunset calculation:', error);
    // Fallback: approximate sunset at 7 PM local time
    const fallbackSunset = new Date(date);
    fallbackSunset.setHours(19, 0, 0, 0);
    return fallbackSunset;
  }
}

export async function getWeeklyParasha(): Promise<string> {
  try {
    // Clear any cached parsha data
    localStorage.removeItem('weekly_parasha_cache');
    
    console.log('Calculating fresh weekly parasha...');
    
    // Get current Israel time
    const now = new Date();
    const israelTime = new Date(now.toLocaleString("en-US", {timeZone: "Asia/Jerusalem"}));
    
    console.log('Current Israel time:', israelTime.toISOString());
    
    // Calculate sunset for today
    const sunset = calculateSunset(israelTime);
    const isAfterSunset = israelTime.getTime() > sunset.getTime();
    
    console.log('Sunset time:', sunset.toISOString());
    console.log('Is after sunset:', isAfterSunset);
    
    // If after sunset, use tomorrow's Hebrew date for parsha calculation
    let targetDate = israelTime;
    if (isAfterSunset) {
      targetDate = new Date(israelTime);
      targetDate.setDate(targetDate.getDate() + 1);
      console.log('Using tomorrow for parsha calculation:', targetDate.toISOString());
    }
    
    const hDate = new HDate(targetDate);
    console.log('Hebrew date:', hDate.toString());
    
    // Calculate parsha using Sedra
    const sedra = new Sedra(hDate.getFullYear(), false); // false for Israel
    
    // Get the upcoming Saturday's parsha
    const saturday = hDate.onOrAfter(6); // Get next Saturday (6 = Saturday)
    console.log('Next Saturday:', saturday.toString());
    
    const parsha = sedra.lookup(saturday);
    console.log('Parsha lookup result:', parsha);
    
    if (parsha && parsha.parsha) {
      let parshaName: string;
      if (Array.isArray(parsha.parsha)) {
        // Handle double parsha (e.g., Vayakhel-Pekudei)
        parshaName = parsha.parsha.map(p => HEBREW_PARSHA_NAMES[p] || p).join('-');
      } else {
        parshaName = HEBREW_PARSHA_NAMES[parsha.parsha] || parsha.parsha;
      }
      
      console.log('Final parsha name:', parshaName);
      return parshaName;
    }
    
    // Fallback
    console.warn('No parsha found, using default');
    return 'בראשית';
    
  } catch (error) {
    console.error('Error getting weekly parasha:', error);
    return 'שגיאה בטעינת פרשת השבוע';
  }
}

export async function getHebrewDate(date: Date): Promise<string> {
  try {
    // Get current Israel time
    const israelTime = new Date(date.toLocaleString("en-US", {timeZone: "Asia/Jerusalem"}));
    
    // Calculate sunset for today
    const sunset = calculateSunset(israelTime);
    const isAfterSunset = israelTime.getTime() > sunset.getTime();
    
    // If after sunset, use tomorrow's Hebrew date
    let targetDate = israelTime;
    if (isAfterSunset) {
      targetDate = new Date(israelTime);
      targetDate.setDate(targetDate.getDate() + 1);
    }
    
    const hDate = new HDate(targetDate);
    
    // Get Hebrew month name
    const monthName = hDate.getMonthName();
    const hebrewMonth = HEBREW_MONTH_NAMES[monthName] || monthName;
    
    // Convert day to Hebrew letters
    const day = hDate.getDate();
    const hebrewDay = day === 1 ? 'א׳' :
                     day === 2 ? 'ב׳' :
                     day === 3 ? 'ג׳' :
                     day === 4 ? 'ד׳' :
                     day === 5 ? 'ה׳' :
                     day === 6 ? 'ו׳' :
                     day === 7 ? 'ז׳' :
                     day === 8 ? 'ח׳' :
                     day === 9 ? 'ט׳' :
                     day === 10 ? 'י׳' :
                     day === 11 ? 'י״א' :
                     day === 12 ? 'י״ב' :
                     day === 13 ? 'י״ג' :
                     day === 14 ? 'י״ד' :
                     day === 15 ? 'ט״ו' :
                     day === 16 ? 'ט״ז' :
                     day === 17 ? 'י״ז' :
                     day === 18 ? 'י״ח' :
                     day === 19 ? 'י״ט' :
                     day === 20 ? 'כ׳' :
                     day === 21 ? 'כ״א' :
                     day === 22 ? 'כ״ב' :
                     day === 23 ? 'כ״ג' :
                     day === 24 ? 'כ״ד' :
                     day === 25 ? 'כ״ה' :
                     day === 26 ? 'כ״ו' :
                     day === 27 ? 'כ״ז' :
                     day === 28 ? 'כ״ח' :
                     day === 29 ? 'כ״ט' :
                     day === 30 ? 'ל׳' :
                     day.toString();
    
    return `${hebrewDay} ${hebrewMonth}`;
  } catch (error) {
    console.error('Error getting Hebrew date:', error);
    return 'תאריך עברי לא זמין';
  }
}

export function getDefaultTitle(type: string): string {
  const titles: Record<string, string> = {
    'times': 'זמני התפילה',
    'prayer-times': 'זמני התפילה',
    'daily-study': 'לימוד יומי',
    'memorial': 'זיכרון',
    'yahrzeit': 'יארצייט',
    'custom': 'דף מותאם אישית',
    'iframe': 'דף חיצוני',
    'video': 'וידאו',
    'rss': 'עדכונים',
    'preset-api': 'נתונים מוכנים',
    'supabase-data': 'נתוני מסד נתונים'
  };
  return titles[type] || 'דף חדש';
}

export function getDefaultContent(type: string): any {
  const content: Record<string, any> = {
    'times': {
      title: 'זמני התפילה',
      times: []
    },
    'prayer-times': {
      title: 'זמני התפילה',
      times: []
    },
    'daily-study': {
      title: 'לימוד יומי',
      lessons: []
    },
    'memorial': {
      title: 'זיכרון',
      message: ''
    },
    'yahrzeit': {
      title: 'יארצייט',
      entries: []
    },
    'custom': {
      title: 'דף מותאם אישית',
      content: ''
    },
    'iframe': {
      title: 'דף חיצוני',
      url: ''
    },
    'video': {
      title: 'וידאו',
      url: ''
    },
    'rss': {
      title: 'עדכונים',
      feedUrl: ''
    },
    'preset-api': {
      title: 'נתונים מוכנים',
      apiUrl: ''
    },
    'supabase-data': {
      title: 'נתוני מסד נתונים',
      query: ''
    }
  };
  return content[type] || { title: 'דף חדש', content: '' };
}
